import {isUnprocessableError} from 'presentation/utils/isUnprocessableError';
import {useGetOdontoDatesQuery} from '../../queries/useGetOdontoDatesQuery';
import {
	HandleHourSelectionParams,
	OdontoAddressModal,
	UseOdontoProfessionalSelectionProps,
} from './types';
import {useQueryClient} from '@tanstack/react-query';
import {QUERY_KEYS} from 'infra/libs/react-query/queryKeys';
import {useEffect, useState} from 'react';
import {useHideSearchBarStore} from 'presentation/modules/booking/shared/store/useHideSearchBarStore';
import {useBookingStore} from 'presentation/modules/booking/shared/store/useBookingStore';
import {formatAddress} from 'presentation/utils';
import {CONSULTATION_CLINIC_ID} from 'presentation/modules/booking/shared/constants/odontoConsultationID';
import {CheckDentalAddressParams} from '../useGetOdontoProviderAddress/types';
import {useGetOdontoProviderAddress} from '../useGetOdontoProviderAddress';
import {useDisabledSelectionDateStore} from 'presentation/modules/booking/shared/store/useDisabledSelectionDateStore';
import {useQueryParams} from 'presentation/pages';
import {useOdontoSearchBar} from 'presentation/modules/booking/odonto/ui/components/OdontoSearchBar/hooks/useOdontoSearchBar';
import {useOdontoBooking} from '../../../../hooks/useOdontoBooking';

export function useOdontoProfessionalSelection({
	handleNextStep,
}: UseOdontoProfessionalSelectionProps) {
	const queryClient = useQueryClient();
	const setHideSearchBarState = useHideSearchBarStore(state => state.setHideSearchBar);
	const setBookingData = useBookingStore(state => state.setBookingData);
	const bookingData = useBookingStore(state => state.bookingData);
	const setDisabledSelectionDate = useDisabledSelectionDateStore(
		state => state.setDisabledSelectionDate,
	);
	const [odontoAddressModal, setOdontoAddressModal] = useState<OdontoAddressModal>('none');
	const {checkDentalAddress} = useGetOdontoProviderAddress();

	const {
		SEARCH_PARAMS: {calendarActive},
	} = useQueryParams();
	const {activeBookingFlowStep} = useOdontoBooking();
	const {handleClearShiftAndSearch} = useOdontoSearchBar({activeStep: activeBookingFlowStep});

	const {
		data: availableDates,
		isFetching: isFetchingAvailableDates,
		error: errorAvailableDates,
		refetch,
	} = useGetOdontoDatesQuery({
		enabled: true,
	});

	const hasNoAvailableDates =
		!isFetchingAvailableDates &&
		(!availableDates || availableDates.length === 0) &&
		!errorAvailableDates;

	const notFoundAvailableDatesError = isUnprocessableError(errorAvailableDates);

	const isLoading = isFetchingAvailableDates;
	const isError = !!errorAvailableDates && !notFoundAvailableDatesError;

	const showNotFoundState = hasNoAvailableDates || notFoundAvailableDatesError;
	const shouldHideSearchBar = !!errorAvailableDates || hasNoAvailableDates;

	const isCalendarActive = !!(calendarActive && calendarActive === 'true');
	const shouldUseUniqueDate = Boolean(isCalendarActive && bookingData.dateAt);

	const showSelectedDateProfessionalGrid = shouldUseUniqueDate;

	const resetAvailableTimesAndDates = () => {
		queryClient.resetQueries({
			queryKey: [QUERY_KEYS.ODONTO_AVAILABLE_TIMES],
		});
		queryClient.resetQueries({
			queryKey: [QUERY_KEYS.ODONTO_DATES],
		});
	};

	const checkOdontoAddressIsValid = async (params: CheckDentalAddressParams) => {
		try {
			setOdontoAddressModal('loading');
			const response = await checkDentalAddress({
				dateAt: params.dateAt,
				professionalCode: params.professionalCode,
				specialtyCode: params.specialtyCode,
				unitCode: params.unitCode,
			});
			setOdontoAddressModal('none');

			return response;
		} catch (error) {
			return setOdontoAddressModal('error');
		}
	};

	const handleSelectHour = async ({
		profCode,
		profName,
		date,
		hour,
		recommendations,
	}: HandleHourSelectionParams) => {
		const checkDental = await checkOdontoAddressIsValid({
			dateAt: date,
			professionalCode: profCode,
			specialtyCode: bookingData.specialtyCode,
			unitCode: bookingData.unitCode,
		});

		const isDentalClinic = bookingData.unitCode === CONSULTATION_CLINIC_ID;

		if (checkDental && isDentalClinic) {
			setBookingData(prev => ({
				...prev,
				unitAddress: formatAddress({
					type: checkDental?.cdTipoLogradouro,
					address: checkDental?.nmRuaEndereco,
					number: checkDental?.nuEndereco.toString(),
					complement: checkDental?.dsComplEndereco,
					district: checkDental?.nmBairroEndereco,
					city: checkDental?.nmCidadeEndereco,
					state: checkDental?.cdUfEndereco,
					zipCode: checkDental?.cdCepEmdereco.toString(),
					reference: checkDental?.dsPontoReferencia,
				}),
			}));
		}

		setBookingData(prev => ({
			...prev,
			professionalCode: profCode,
			professionalName: profName,
			dateAt: date,
			hourAt: hour,
			odontoRecommendations:
				recommendations && recommendations.length > 0
					? recommendations[0]
					: prev.odontoRecommendations,
		}));

		handleNextStep();
	};

	useEffect(() => {
		setHideSearchBarState(shouldHideSearchBar);
	}, [shouldHideSearchBar, setHideSearchBarState]);

	useEffect(() => {
		resetAvailableTimesAndDates();
	}, []);

	useEffect(() => {
		setDisabledSelectionDate(odontoAddressModal !== 'none');
	}, [odontoAddressModal, setDisabledSelectionDate]);

	return {
		isLoading,
		refetch,
		isError,
		showNotFoundState,
		handleSelectHour,
		odontoAddressModal,
		setOdontoAddressModal,
		showSelectedDateProfessionalGrid,
		handleClearShiftAndSearch,
		availableDates,
	};
}
